import classes from "./Chart.module.css";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import AUTH_CONTEXT from "../../store";
import { StatusCode } from "../../utils";
import ARENO_API_ENDPOINT from "../../api";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { ACTIVITY_STATS_FILTER_OPTIONS } from "./chartFilterOptions";

const UserActivityChart = ({ aspect, title }) => {
  const { token } = AUTH_CONTEXT();
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [data, setData] = useState([]);
  const [TimeDuration, setTimeDuration] = useState("currentWeek");
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [Service, setService] = useState("all");
  const hasLoadedRef = useRef(false);

  const handleServiceChange = (event) => {
    event.preventDefault();
    setService(event.target.value);
    setIsDataFetched(false);
    hasLoadedRef.current = false; // Reset cache when filter changes
  };

  const handleRefresh = () => {
    setIsDataFetched(false);
    setRefreshTrigger(prev => prev + 1);
  };

  // Map TimeDuration to API period format
  const getPeriodParams = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11

    switch (TimeDuration) {
      case "currentWeek":
        return { period: "currentWeek" };
      case "daily":
        return { period: "daily", year: currentYear, month: currentMonth };
      case "weekly":
        return { period: "weekly", year: currentYear, month: currentMonth };
      case "monthly":
        return { period: "monthly", year: currentYear };
      case "yearly":
        return { period: "yearly" };
      default:
        return { period: "currentWeek" };
    }
  };

  const FetchApi = async (isManualRefresh = false) => {
    try {
      const periodParams = getPeriodParams();
      const requestBody = {
        period: periodParams.period,
        filterType: Service,
        ...(periodParams.year && { year: periodParams.year }),
        ...(periodParams.month && { month: periodParams.month }),
      };

      let response = await fetch(
        `${ARENO_API_ENDPOINT}/challenges/AdminGetUserActivityStats`,
        {
          method: "POST",
          body: JSON.stringify(requestBody),
          headers: {
            "Content-Type": "application/json",
            authorization: "Bearer" + " " + token,
          },
        }
      );

      StatusCode(response, navigate);
      const responseData = await response.json();

      if (responseData.status === 200 && responseData.data) {
        setIsDataFetched(true);
        // The API returns data directly as an array from generateBreakdown
        // Data format: [{ Key: number, Count: number }]
        const transformedData = Array.isArray(responseData.data)
          ? responseData.data.map((item) => ({
            Key: item.Key !== undefined ? item.Key : (item.label || item.day || item.date || item.period || ""),
            Count: item.Count !== undefined ? item.Count : (item.count || item.value || 0),
          }))
          : [];
        console.log("Transformed chart data:", transformedData);

        // Only show success toast on manual refresh
        if (isManualRefresh) {
          toast.success("User activity data fetched successfully!", {
            position: "bottom-left",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
        }

        return transformedData;
      } else {
        setIsDataFetched(false);
        const errorMsg = responseData.error || "Failed to fetch data";
        console.error(errorMsg);
        alert(errorMsg);
        return [];
      }
    } catch (e) {
      setIsDataFetched(false);
      console.log(e.message);
      alert(e.message);
      return [];
    }
  };

  useEffect(() => {
    async function FetchData() {
      try {
        // Determine if this is a manual refresh (triggered by refreshTrigger change from handleRefresh)
        const isManualRefresh = refreshTrigger > 0;
        const currentCacheKey = `userActivityChart_${TimeDuration}_${Service}`;

        // Check cache first (unless it's a manual refresh)
        // Always check cache on normal navigation, even if hasLoadedRef is false (component remounted)
        if (!isManualRefresh) {
          const cachedData = sessionStorage.getItem(currentCacheKey);
          if (cachedData) {
            try {
              const parsedData = JSON.parse(cachedData);
              setData(parsedData);
              setIsDataFetched(true);
              hasLoadedRef.current = true;
              return; // Use cached data, don't fetch
            } catch (e) {
              console.error("Error parsing cached data:", e);
            }
          }
        }

        // Clear cache only on manual refresh
        if (isManualRefresh) {
          sessionStorage.removeItem(currentCacheKey);
        }

        let result = await FetchApi(isManualRefresh);
        if (result && Array.isArray(result)) {
          console.log("Setting chart data:", result);
          setData(result);
          // Cache the data
          sessionStorage.setItem(currentCacheKey, JSON.stringify(result));
          hasLoadedRef.current = true;
        } else {
          console.log("No valid data received, result:", result);
          setData([]);
        }
      } catch (err) {
        console.log(err.message);
        setIsDataFetched(false);
        setData([]);
      }
    }

    // Check if page was reloaded (only on initial mount, not on filter changes)
    if (refreshTrigger === 0) {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      const isPageReload = navigationEntry?.type === 'reload';
      if (isPageReload) {
        hasLoadedRef.current = false;
        const currentCacheKey = `userActivityChart_${TimeDuration}_${Service}`;
        sessionStorage.removeItem(currentCacheKey);
      }
    }

    FetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TimeDuration, Service, refreshTrigger]);

  const TimeDurationHandler = (event) => {
    event.preventDefault();
    setTimeDuration(event.target.value);
    setIsDataFetched(false);
    hasLoadedRef.current = false; // Reset cache when filter changes
  };

  // Get the bottom heading label based on selected period
  const getBottomHeading = () => {
    switch (TimeDuration) {
      case "currentWeek":
        return "Date";
      case "daily":
        return "Date";
      case "weekly":
        return "Week";
      case "monthly":
        return "Month";
      case "yearly":
        return "Year";
      default:
        return "Date";
    }
  };

  return (
    <div className={classes.chart}>
      <div className={classes.title}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className={classes.subtitle}>{title}</div>
          <IconButton
            onClick={handleRefresh}
            disabled={!isDataFetched}
            size="small"
            sx={{
              color: '#412A7B',
              '&:hover': {
                backgroundColor: 'rgba(65, 42, 123, 0.1)',
              },
              '&.Mui-disabled': {
                color: '#cbd5e1',
              },
            }}
          >
            <RefreshIcon
              sx={{
                fontSize: '20px',
                animation: !isDataFetched ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
          </IconButton>
        </div>
        <div>
          <div className={classes.selectbox}>
            <div className={classes.selectWrapper}>
              <label htmlFor="CategoryName" className={classes.label}>Duration: </label>
              <select
                name="CategoryName"
                id="CategoryName"
                className={classes.select}
                value={TimeDuration}
                onChange={(e) => TimeDurationHandler(e)}
              >
                <option value="currentWeek">Current Week</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className={classes.selectWrapper}>
              <label htmlFor="FilterType" className={classes.label}>Filter: </label>
              <select
                name="FilterType"
                id="FilterType"
                className={classes.select}
                value={Service}
                onChange={(e) => handleServiceChange(e)}
              >
                {ACTIVITY_STATS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {
        isDataFetched ? (
          <div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart
                className={classes.AreaChart}
                width={650}
                height={250}
                data={data}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="modernGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#412A7B" stopOpacity={0.8} />
                    <stop offset="50%" stopColor="#6B4FA3" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#FCB401" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="Key"
                  stroke="#64748b"
                  strokeWidth={1}
                  style={{ fontSize: '12px', fontWeight: 500 }}
                  tick={{ fill: '#475569' }}
                  axisLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                />
                <YAxis
                  dataKey="Count"
                  stroke="#64748b"
                  strokeWidth={1}
                  style={{ fontSize: '12px', fontWeight: 500 }}
                  tick={{ fill: '#475569' }}
                  axisLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                />
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#cbd5e1"
                  strokeWidth={1}
                  opacity={0.8}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    color: '#1e293b',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    padding: '12px',
                  }}
                  labelStyle={{ color: '#412A7B', fontWeight: 600, fontSize: '14px' }}
                  itemStyle={{ color: '#1e293b', fontWeight: 500 }}
                />
                <Area
                  type="monotone"
                  dataKey="Count"
                  stroke="#412A7B"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#modernGradient)"
                  dot={{ fill: '#412A7B', r: 4, strokeWidth: 2, stroke: '#ffffff' }}
                  activeDot={{ r: 6, fill: '#FCB401', strokeWidth: 2, stroke: '#ffffff' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className={classes.loaderContainer}>
            <CircularProgress sx={{ color: '#412A7B' }} size={40} />
          </div>
        )
      }
      <div className={classes.BottomHeading}>{getBottomHeading()}</div>
    </div >
  );
};

export default UserActivityChart;
